import { useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Download, CalendarDays, CheckCircle2, ArrowUpRight, X, Loader2, Lock, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PLAYBOOKS } from "@/data/playbooks";
import { Seo, articleSchema, howToSchema, breadcrumbSchema, organizationSchema } from "@/components/Seo";

const REVIEW_CALL_URL = "https://calendar.app.google/i1x5bUKAX7oPD9Mu8";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LEAD_CACHE_KEY = (slug) => `sideb.playbook_lead.${slug}`;

export default function Playbook() {
  const { slug } = useParams();
  const playbook = PLAYBOOKS[slug];
  const [showLeadModal, setShowLeadModal] = useState(false);

  if (!playbook) {
    return <Navigate to="/parse" replace />;
  }

  const openDownload = () => {
    // If the visitor has already submitted the form for this slug in this browser,
    // skip the modal and open the PDF directly.
    try {
      if (localStorage.getItem(LEAD_CACHE_KEY(slug))) {
        window.open(`${BACKEND_URL}/api/playbooks/${slug}/download.pdf`, "_blank", "noopener,noreferrer");
        return;
      }
    } catch { /* localStorage unavailable — fall through to modal */ }
    setShowLeadModal(true);
  };

  return (
    <div className="bg-white" data-testid={`playbook-page-${slug}`}>
      <Seo
        title={`${playbook.title} · SideB Playbook`}
        description={playbook.intro}
        canonical={`https://sideb.io/playbooks/${slug}`}
        ogType="article"
        schema={[
          articleSchema({
            headline: playbook.title,
            description: playbook.intro,
            slug: `playbooks/${slug}`,
            datePublished: "2026-01-01",
            section: "Playbook",
          }),
          howToSchema({
            name: playbook.title,
            description: playbook.intro,
            steps: playbook.sections,
          }),
          breadcrumbSchema([
            { name: "Home", url: "https://sideb.io/" },
            { name: "Playbooks", url: "https://sideb.io/parse" },
            { name: playbook.title, url: `https://sideb.io/playbooks/${slug}` },
          ]),
          organizationSchema(),
        ]}
      />

      {/* HERO */}
      <section className="border-b border-border bg-[#0a1628] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-10">
            <div className="max-w-3xl">
              <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#b967ff] mb-5" data-testid="playbook-eyebrow">
                {playbook.eyebrow}
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-[56px] font-semibold tracking-[-0.035em] leading-[1.04]">
                {playbook.title}
              </h1>
              <p className="mt-4 text-[15px] font-mono uppercase tracking-[0.14em] text-white/55" data-testid="playbook-audience">
                For {playbook.audience}
              </p>
              <p className="mt-6 text-base text-white/75 leading-relaxed max-w-2xl">
                {playbook.intro}
              </p>
            </div>

            <div className="flex flex-col gap-3 lg:items-end shrink-0">
              <Button
                onClick={openDownload}
                className="rounded-full bg-[#b967ff] hover:bg-[#a855f7] h-11 px-6 text-sm font-medium"
                data-testid="playbook-download-pdf"
              >
                <Download className="mr-2 h-4 w-4" />
                Download as PDF
              </Button>
              <Button asChild variant="outline" className="rounded-full h-11 px-6 text-sm font-medium border-white/20 text-white hover:bg-white/10 hover:text-white hover:border-white/40" data-testid="playbook-schedule-call">
                <a href={REVIEW_CALL_URL} target="_blank" rel="noopener noreferrer">
                  <CalendarDays className="mr-2 h-4 w-4" />
                  Schedule a call
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* STATS BAND (only shown when the playbook exposes `stats`) */}
      {playbook.stats?.length ? (
        <section className="bg-white border-b border-border" data-testid="playbook-stats">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
              {playbook.stats.map((s, i) => (
                <div key={`stat-${i}-${s.label}`} className="flex flex-col gap-1" data-testid={`playbook-stat-${i + 1}`}>
                  <div className="font-display text-2xl md:text-3xl font-semibold tracking-[-0.02em] text-[#0a1628]">
                    {s.label}
                  </div>
                  <div className="text-xs font-mono uppercase tracking-[0.12em] text-foreground/55">
                    {s.detail}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* WHAT'S INSIDE (only shown when the playbook exposes `whatsInside`) */}
      {playbook.whatsInside?.length ? (
        <section className="border-b border-border bg-[#faf7ff]" data-testid="playbook-whats-inside">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-16">
            <div className="grid lg:grid-cols-[1fr_auto] gap-8 lg:gap-16 items-start">
              <div className="max-w-3xl">
                <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#ac62ea] mb-3">
                  What&rsquo;s inside the PDF
                </div>
                <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-[-0.025em] leading-[1.1] mb-6">
                  The full framework, the scorecard, and the eight patterns — in one document.
                </h2>
                <ul className="space-y-3">
                  {playbook.whatsInside.map((item, i) => (
                    <li key={`inside-${i}`} className="flex items-start gap-3">
                      <FileText className="w-[18px] h-[18px] text-[#ac62ea] shrink-0 mt-0.5" />
                      <span className="text-sm leading-relaxed text-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:sticky lg:top-24 lg:w-[280px] rounded-2xl bg-white border border-border p-6 shadow-sm">
                <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-foreground/55 mb-2">Playbook</div>
                <div className="font-display text-lg font-semibold leading-snug tracking-[-0.015em] mb-4">
                  Get the full PDF
                </div>
                <p className="text-xs text-foreground/60 leading-relaxed mb-5">
                  Delivered instantly. No newsletter drip. We&rsquo;ll only follow up if it looks like a genuine fit.
                </p>
                <Button
                  onClick={openDownload}
                  className="w-full rounded-full bg-[#b967ff] hover:bg-[#a855f7] h-11 text-sm font-medium"
                  data-testid="playbook-inside-download"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download as PDF
                </Button>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* SECTIONS */}
      <section>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="space-y-14 max-w-3xl">
            {playbook.sections.map((section, idx) => (
              <PlaybookSection
                key={section.title}
                section={section}
                index={idx}
                onDownload={openDownload}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#faf7ff] border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-[-0.025em] leading-[1.1]">
              {playbook.cta.heading}
            </h2>
            <p className="mt-4 text-base text-muted-foreground leading-relaxed max-w-2xl">
              {playbook.cta.sub}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild className="rounded-full bg-[#0b1e5a] hover:bg-black h-11 px-6 text-sm font-medium" data-testid="playbook-cta-schedule">
                <a href={REVIEW_CALL_URL} target="_blank" rel="noopener noreferrer">
                  <CalendarDays className="mr-2 h-4 w-4" />
                  Schedule a call
                  <ArrowUpRight className="ml-1.5 h-4 w-4" />
                </a>
              </Button>
              <Link to="/parse" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#ac62ea] hover:underline" data-testid="playbook-retake">
                Take the assessment for another role
              </Link>
            </div>
          </div>
        </div>
      </section>

      {showLeadModal && (
        <LeadGateModal
          slug={slug}
          playbookTitle={playbook.title}
          onClose={() => setShowLeadModal(false)}
        />
      )}
    </div>
  );
}

/* ------------------------------ SECTION RENDERER ------------------------------ */

const PlaybookSection = ({ section, index, onDownload }) => {
  const number = String(index + 1).padStart(2, "0");

  if (section.locked) {
    const showUnlockButton = Boolean(section.unlockCta);
    return (
      <article className="group relative" data-testid={`playbook-section-${index + 1}`}>
        <div className="flex items-baseline gap-4 mb-3">
          <span className="font-mono text-xs text-[#ac62ea] font-semibold tracking-[0.14em]">
            {number}
          </span>
          <h2 className="font-display text-[26px] sm:text-3xl font-semibold tracking-[-0.025em] leading-[1.15]">
            {section.title}
          </h2>
        </div>
        <p className="text-[15px] text-muted-foreground leading-relaxed mb-6 ml-10">
          {section.summary}
        </p>

        <div className="ml-10 rounded-2xl border border-[#ac62ea]/25 bg-gradient-to-br from-[#faf7ff] to-white p-6 sm:p-7 relative overflow-hidden">
          <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] text-[#ac62ea] mb-4">
            <Lock className="w-3.5 h-3.5" />
            Inside the PDF
          </div>
          {section.preview?.length ? (
            <ul className={`space-y-2.5 ${showUnlockButton ? "mb-6" : ""}`}>
              {section.preview.map((item, pIdx) => (
                <li key={`${section.title}-preview-${pIdx}`} className="flex items-start gap-3">
                  <span className="w-1 h-1 rounded-full bg-[#ac62ea] mt-2.5 shrink-0" />
                  <span className="text-sm leading-relaxed text-foreground/85">{item}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {showUnlockButton ? (
            <Button
              onClick={onDownload}
              className="rounded-full bg-[#b967ff] hover:bg-[#a855f7] h-10 px-5 text-sm font-medium"
              data-testid={`playbook-section-${index + 1}-unlock`}
            >
              <Download className="mr-2 h-4 w-4" />
              {section.unlockCta}
            </Button>
          ) : null}
        </div>
      </article>
    );
  }

  return (
    <article className="group" data-testid={`playbook-section-${index + 1}`}>
      <div className="flex items-baseline gap-4 mb-3">
        <span className="font-mono text-xs text-[#ac62ea] font-semibold tracking-[0.14em]">
          {number}
        </span>
        <h2 className="font-display text-[26px] sm:text-3xl font-semibold tracking-[-0.025em] leading-[1.15]">
          {section.title}
        </h2>
      </div>
      <p className="text-[15px] text-muted-foreground leading-relaxed mb-6 ml-10">
        {section.summary}
      </p>
      <ul className="space-y-3 ml-10">
        {section.actions.map((action, aIdx) => (
          <li key={`${section.title}-action-${aIdx}`} className="flex items-start gap-3">
            <CheckCircle2 className="w-[18px] h-[18px] text-[#ac62ea] shrink-0 mt-0.5" />
            <span className="text-sm leading-relaxed text-foreground">{action}</span>
          </li>
        ))}
      </ul>
    </article>
  );
};

/* ------------------------------ LEAD GATE MODAL ------------------------------ */

const LeadGateModal = ({ slug, playbookTitle, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", company_url: "" });
  const [submitting, setSubmitting] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      // The endpoint stores the lead, notifies the partner-desk, and streams the
      // PDF back in the same response — so we handle the file directly here.
      const res = await axios.post(
        `${BACKEND_URL}/api/playbooks/${slug}/lead-gen`,
        form,
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sideb-playbook-${slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      try {
        localStorage.setItem(LEAD_CACHE_KEY(slug), JSON.stringify({ ts: Date.now(), email: form.email }));
      } catch { /* localStorage unavailable — the current download still works */ }
      toast.success("Downloading — check your downloads folder.");
      onClose();
    } catch (err) {
      toast.error("Something went wrong. Please try again or email rev@sideb.io.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="playbook-lead-modal"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-md shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-foreground/40 hover:text-foreground transition-colors"
          aria-label="Close"
          data-testid="playbook-lead-modal-close"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="p-8">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-foreground/55 mb-3">
            Playbook download
          </div>
          <h3 className="font-display text-2xl font-semibold tracking-[-0.02em] leading-snug mb-2">
            {playbookTitle}
          </h3>
          <p className="text-sm text-foreground/65 leading-relaxed mb-6">
            Tell us who you are and we&rsquo;ll send the PDF straight to your download folder. We&rsquo;ll only follow up if it looks like a genuine fit.
          </p>
          <form onSubmit={submit} className="space-y-4" data-testid="playbook-lead-form">
            <label className="block">
              <span className="block text-xs font-medium text-foreground/70 mb-1.5">Your name *</span>
              <input
                type="text"
                value={form.name}
                onChange={update("name")}
                required
                data-testid="playbook-lead-name"
                className="w-full rounded-lg border border-border bg-white px-3.5 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[#ac62ea]/40 focus:border-[#ac62ea] transition-colors"
              />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground/70 mb-1.5">Work email *</span>
              <input
                type="email"
                value={form.email}
                onChange={update("email")}
                required
                data-testid="playbook-lead-email"
                className="w-full rounded-lg border border-border bg-white px-3.5 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[#ac62ea]/40 focus:border-[#ac62ea] transition-colors"
              />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground/70 mb-1.5">Company URL <span className="text-foreground/40">(optional)</span></span>
              <input
                type="text"
                placeholder="https://"
                value={form.company_url}
                onChange={update("company_url")}
                data-testid="playbook-lead-company-url"
                className="w-full rounded-lg border border-border bg-white px-3.5 py-2.5 text-sm text-foreground placeholder:text-foreground/40 focus:outline-none focus:ring-2 focus:ring-[#ac62ea]/40 focus:border-[#ac62ea] transition-colors"
              />
            </label>
            <div className="pt-2">
              <Button
                type="submit"
                disabled={submitting}
                className="w-full rounded-full bg-[#b967ff] hover:bg-[#a855f7] h-11 text-sm font-medium"
                data-testid="playbook-lead-submit"
              >
                {submitting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Preparing PDF…</>
                ) : (
                  <><Download className="mr-2 h-4 w-4" /> Get the PDF</>
                )}
              </Button>
            </div>
            <p className="text-[11px] text-foreground/45 leading-relaxed text-center pt-1">
              We never sell your data. See our <Link to="/privacy" className="underline hover:text-[#ac62ea]">Privacy notice</Link>.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};
